import { Userpilot } from 'userpilot';
import { call, takeEvery, take, select } from 'redux-saga/effects';
import {
  AUTH_SUCCESS,
  LOGOUT,
  SIGNUP_SUCCESS,
  VALIDATE_CREDENTIALS_SUCCESS,
  VALIDATE_TOKEN_SUCCESS
} from 'JS/actionCreators/authActions';
import { LOCATION_CHANGE } from 'connected-react-router';
import { E2E_GEN_TEST_USER_PREFIX, E2E_GEN_TEST_USER_SUFFIX } from 'JS/config/config';
import {
  ADD_SCRIBE_SUCCESS,
  CREATE_SCENE,
  DELETE_SCRIBE_SUCCESS,
  DUPLICATE_SELECTED_SCENES,
  LOAD_SCRIBES_SUCCESS,
  SAVE_AUDIO
} from 'JS/actionCreators/scribeActions';
import {
  CLOSE_USERPERSONA_MODAL,
  DISMISS_USERPERSONA_MODAL,
  SUBMIT_USERPERSONA_MODAL
} from 'JS/actionCreators/marketingActions';
import { TRACK_PLAY_SCRIBE, TRACK_TEMPLATE_EDIT } from 'JS/actionCreators/trackingActions';
import getAudioFileDuration from 'JS/shared/helpers/getAudioFileDuration';
import {
  DOWNLOAD_REQUEST,
  DOWNLOAD_REQUEST_FAILED,
  GET_VIDEO_DOWNLOAD_URL_FAILED,
  GET_VIDEO_DOWNLOAD_URL_SUCCESS,
  RENDER_FAILED
} from 'JS/actionCreators/downloadActions';
import getFileTypeFromFormat from 'JS/shared/helpers/getFileTypeFromFormat';
import getScribeLengthMs from 'JS/playback/lib/Playback/helpers/getScribeLengthMs';
import { HIDE_MODAL } from 'JS/actionCreators/uiActions';
import {
  TRACK_UPGRADE_PAY_NOW_CLICK,
  TRACK_PAYMENT_SUCCESS,
  TRACK_PAYMENT_FAILED
} from 'JS/actionCreators/trackingActions';
import {
  UPGRADE_EXISTING_ACCOUNT_REQUESTED,
  UPGRADE_EXISTING_ACCOUNT_SUCCESS,
  UPGRADE_EXISTING_ACCOUNT_FAILURE
} from 'JS/actionCreators/paymentActions';
import { DUPLICATE_SCRIBE_SUCCESS } from 'JS/actionCreators/duplicateScribeActions';

import { getScribeById, getUser } from '../selectors';

const debug = !!process.env.DEBUG_USERPILOT;
const logger = debug ? (...args) => console.log(`%c[Userpilot Debug]: `, 'color: #f75a79', ...args) : () => {};
const isE2eTestUser = email => email.includes(E2E_GEN_TEST_USER_PREFIX) && email.includes(E2E_GEN_TEST_USER_SUFFIX);

Userpilot.initialize(process.env.USERPILOT_KEY);
let identified;

function userpilotInitializeWithLogger(id, config) {
  Userpilot.identify(id, config);
  identified = true;
  logger(`Identified user ${id} with config`, config);
}

function userpilotTrackWithLogger(eventName, properties) {
  Userpilot.track(eventName, properties);
  logger(`Track event - ${eventName}`, properties);
}

function identifiedOnlyFn(fn) {
  return (...args) => {
    if (identified) {
      return fn.apply(null, args);
    }
  };
}

function* userpilotIdentify({ user, type }) {
  if (!isE2eTestUser(user.email)) {
    const userConfig = {
      email: user.email,
      name: user.firstname,
      'Subscription Tier': user.accountType,
      'Licence Type': user.accountType
    };

    yield call(userpilotInitializeWithLogger, user.id, userConfig);
  }
}

function* userpilotSignup({ email }) {
  if (!isE2eTestUser(email)) {
    const signupDate = new Date();
    const { user } = yield take(AUTH_SUCCESS);
    const userConfig = {
      email: user.email,
      name: user.firstname,
      created_at: Math.floor(signupDate.getTime() / 1000),
      'Subscription Tier': user.accountType,
      'Licence Type': user.accountType,
      'Signup Date': signupDate
    };

    yield call(userpilotInitializeWithLogger, user.id, userConfig);
  }
}

function logout() {
  identified = false;
}

function* userpilotReload() {
  yield call(Userpilot.reload);
  logger(`Reloading Userpilot`);
}

const updateScribeCount = identifiedOnlyFn(function*(action) {
  const user = yield select(getUser);
  const scribes = yield select(state => state.scribes.allScribes);
  yield call(userpilotInitializeWithLogger, user.id, {
    'Number of Scribes Created': scribes.length
  });

  if (action.type === ADD_SCRIBE_SUCCESS && action.scribe?.source?.type === 'blank') {
    const { scribe } = action;
    yield call(userpilotTrackWithLogger, 'Create Blank Scribe', {
      'Canvas Size': scribe.canvasSize
    });
  }
});

const trackDismissUserPersonaModal = identifiedOnlyFn(function*() {
  yield call(userpilotTrackWithLogger, 'Dismissed User Persona Modal');
});

const trackSubmitUserPersonaModal = identifiedOnlyFn(function*({ answers }) {
  yield take(CLOSE_USERPERSONA_MODAL);
  const organization = answers.user_type === 'other' ? `other:${answers.user_type_text}` : answers.user_type;
  yield call(userpilotTrackWithLogger, 'Completed User Persona Modal', {
    Organization: organization,
    Intentions: answers.intention
  });

  const user = yield select(getUser);
  yield call(userpilotInitializeWithLogger, user.id, {
    'User Organisation': organization,
    'User Intentions': answers.intention
  });
});

const trackEditTemplate = identifiedOnlyFn(function*({ title, category, triggerLocation }) {
  yield call(userpilotTrackWithLogger, 'Edit Template', {
    'Template Title': title,
    'Template Category': category,
    'Button Location': triggerLocation
  });
});

const trackAddingScene = identifiedOnlyFn(function*() {
  yield call(userpilotTrackWithLogger, 'Add Scene');
});

const trackDuplicateScene = identifiedOnlyFn(function*() {
  yield call(userpilotTrackWithLogger, 'Duplicate Scene');
});

const trackAudio = identifiedOnlyFn(function*({ audio, source, trackLength, audioType }) {
  let duration = trackLength;
  try {
    if (!duration) {
      duration = yield call(getAudioFileDuration, audio);
    }
  } catch (error) {
    console.error(error);
  }

  yield call(userpilotTrackWithLogger, 'Update Audio', {
    'Audio Length': duration,
    'Audio Source': source,
    'Audio Type': audioType
  });
});

const trackPlayScribe = identifiedOnlyFn(function*({ button, sceneCount }) {
  if (button === 'fromStart') {
    yield call(userpilotTrackWithLogger, 'Play From Video Start', {
      'Number Of Scenes': sceneCount
    });
  }
});

const trackDownload = identifiedOnlyFn(function*({ format, scribeId }) {
  const scribe = yield select(state => getScribeById(state, scribeId));
  const numberOfElements = scribe.elements?.length || 0;
  const numberOfScenes = scribe.scenes?.length || 0;
  const scribeLengthSeconds = getScribeLengthMs(scribe) / 1000;
  const fileType = getFileTypeFromFormat(format);
  const { source } = scribe;
  let templateProperties = {};

  if (source?.type === 'template') {
    templateProperties['Scribe Template ID'] = source.meta?.templateId;
    templateProperties['Scribe Template Title'] = source.meta?.title;
  }

  const eventProperties = {
    'File Type': fileType,
    'Scribe Type': source?.type || 'unknown',
    'Scribe Length': scribeLengthSeconds,
    'Number of Scenes': numberOfScenes,
    'Number of Elements': numberOfElements,
    ...templateProperties
  };

  const result = yield take([
    HIDE_MODAL,
    GET_VIDEO_DOWNLOAD_URL_SUCCESS,
    RENDER_FAILED,
    GET_VIDEO_DOWNLOAD_URL_FAILED,
    DOWNLOAD_REQUEST_FAILED
  ]);

  if (result.type === GET_VIDEO_DOWNLOAD_URL_SUCCESS) {
    yield call(userpilotTrackWithLogger, 'Download Succeeded', eventProperties);
  }
});

const trackUpgradeSuccess = identifiedOnlyFn(function*() {
  const { isoCode3: country, currency } = yield select(({ location }) => location);

  const eventProperties = {
    'Payment Country': country,
    Currency: currency
  };

  const result = yield take([
    TRACK_PAYMENT_SUCCESS,
    UPGRADE_EXISTING_ACCOUNT_SUCCESS,
    TRACK_PAYMENT_FAILED,
    UPGRADE_EXISTING_ACCOUNT_FAILURE
  ]);

  if (result.type === TRACK_PAYMENT_SUCCESS || result.type === UPGRADE_EXISTING_ACCOUNT_SUCCESS) {
    yield call(userpilotTrackWithLogger, 'Upgrade Succeeded', eventProperties);
  }
});

export default function* userpilotSagas() {
  yield takeEvery(LOCATION_CHANGE, userpilotReload);
  yield takeEvery(SIGNUP_SUCCESS, userpilotSignup);
  yield takeEvery([VALIDATE_CREDENTIALS_SUCCESS, VALIDATE_TOKEN_SUCCESS], userpilotIdentify);
  yield takeEvery(LOGOUT, logout);
  yield takeEvery(
    [LOAD_SCRIBES_SUCCESS, ADD_SCRIBE_SUCCESS, DELETE_SCRIBE_SUCCESS, DUPLICATE_SCRIBE_SUCCESS],
    updateScribeCount
  );
  yield takeEvery(DISMISS_USERPERSONA_MODAL, trackDismissUserPersonaModal);
  yield takeEvery(SUBMIT_USERPERSONA_MODAL, trackSubmitUserPersonaModal);
  yield takeEvery(TRACK_TEMPLATE_EDIT, trackEditTemplate);
  yield takeEvery(CREATE_SCENE, trackAddingScene);
  yield takeEvery(DUPLICATE_SELECTED_SCENES, trackDuplicateScene);
  yield takeEvery(SAVE_AUDIO, trackAudio);
  yield takeEvery(TRACK_PLAY_SCRIBE, trackPlayScribe);
  yield takeEvery(DOWNLOAD_REQUEST, trackDownload);
  yield takeEvery([TRACK_UPGRADE_PAY_NOW_CLICK, UPGRADE_EXISTING_ACCOUNT_REQUESTED], trackUpgradeSuccess);
}
